import { useCallback, useState } from "react";
import Api from "../utils/api";
import IModalidadPago from "../interfaces/IModalidadPago";

// Etiquetas de botón según recaudadora (definidas en el mail de cambio).
const RECAUDADORA_LABEL: Record<string, string> = {
  MERCADOPAGO: "Mercadopago",
  DECIDIR: "Pago con tarjeta crédito",
};

export const recaudadoraLabel = (rec?: string, fallback = ""): string => {
  const key = (rec || "").toUpperCase();
  return RECAUDADORA_LABEL[key] ?? fallback ?? rec ?? "";
};

interface Urls {
  url_success: string;
  url_cancel: string;
  url_redirect: string;
}

interface FetchParams {
  prm_co_pasajero: string;
  prm_talones: string;
  prm_importe_ctl: number | string;
  urls: Urls;
}

// Array de medios de pago en la respuesta de modalidadPago.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractModalidades = (res: any): IModalidadPago[] => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const candidate = res.PRM_MEDIOS_PAGOS ?? res.MEDIOS_PAGOS ?? res.data;
  return Array.isArray(candidate) ? candidate : [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractUrl = (res: any): string | null =>
  res?.url ??
  res?.URL ??
  res?.init_point ??
  res?.sandbox_init_point ??
  res?.PD_URL ??
  res?.pd_url ??
  res?.URL_PAGO ??
  res?.url_pago ??
  res?.PURL ??
  res?.link ??
  null;

// El backend puede mandar el mensaje de error en distintos campos y en HTML.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractErrorMsg = (res: any): string => {
  const raw = res?.error ?? res?.PD_ERROR ?? res?.PM_ERROR ?? "";
  if (!raw || typeof raw !== "string") return "";
  // Decodifica entidades HTML y quita tags (ej: "<h2>&iexcl;ups...").
  const tmp = document.createElement("div");
  tmp.innerHTML = raw;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

// Corta la espera si el backend no responde (ej: MP colgado generando la preferencia).
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("timeout")),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });

export default function usePagoOnline(jwt?: string) {
  const [modalidades, setModalidades] = useState<IModalidadPago[]>([]);
  const [loadingModalidades, setLoadingModalidades] = useState(false);
  // Se rastrea por TOKEN (no por recaudadora) porque DECIDIR trae varios registros.
  const [payingToken, setPayingToken] = useState<string | null>(null);
  const [error, setError] = useState("");

  // Paso 1: consumir → modalidadPago
  const fetchModalidades = useCallback(
    async (params: FetchParams): Promise<IModalidadPago[]> => {
      setError("");
      setLoadingModalidades(true);
      try {
        const res = await Api("consumir", jwt).post({
          servicio: "modalidadPago",
          param: {
            prm_co_pasajero: params.prm_co_pasajero,
            prm_talones: params.prm_talones,
            prm_importe_ctl: String(params.prm_importe_ctl),
            url_success: params.urls.url_success,
            url_cancel: params.urls.url_cancel,
            url_redirect: params.urls.url_redirect,
          },
        });
        // Log temporal para inspeccionar la forma real en pruebas.
        console.log("[pagoOnLine] modalidadPago →", res);

        const list = extractModalidades(res);
        if (!list.length) {
          setError(
            extractErrorMsg(res) || "No hay modalidades de pago disponibles."
          );
        }
        setModalidades(list);
        return list;
      } catch (e) {
        console.error("[pagoOnLine] error modalidadPago", e);
        setError("No pudimos obtener las modalidades de pago. Probá de nuevo.");
        setModalidades([]);
        return [];
      } finally {
        setLoadingModalidades(false);
      }
    },
    [jwt]
  );

  // Paso 2: pagoOnLine → devuelve la URL de la recaudadora
  const iniciarPago = useCallback(
    async (modalidad: IModalidadPago): Promise<string | null> => {
      setError("");
      setPayingToken(modalidad.TOKEN);
      try {
        const res = await withTimeout(
          Api("pagoOnLine", jwt).post({
            recaudadora: modalidad.RECAUDADORA,
            pagoToken: modalidad.TOKEN,
          }),
          30000
        );
        console.log("[pagoOnLine] pagoOnLine →", res);

        const url = extractUrl(res);
        if (!url) {
          setError(extractErrorMsg(res) || "No recibimos la URL de pago.");
          return null;
        }
        return url;
      } catch (e) {
        console.error("[pagoOnLine] error pagoOnLine", e);
        const msg =
          e instanceof Error && e.message === "timeout"
            ? "El pago está tardando más de lo normal. Probá de nuevo en un momento."
            : "Ocurrió un error procesando el pago. Intentá nuevamente.";
        setError(msg);
        return null;
      } finally {
        setPayingToken(null);
      }
    },
    [jwt]
  );

  const reset = useCallback(() => {
    setModalidades([]);
    setError("");
    setPayingToken(null);
  }, []);

  return {
    modalidades,
    loadingModalidades,
    payingToken,
    error,
    setError,
    fetchModalidades,
    iniciarPago,
    reset,
  };
}
